import { useCallback } from "react";
import { Bundle } from "../types";
import { useAccountData } from "./useAccountData";
import { FUNNEL_TYPES } from "../components/funnel/utils/contants";
import {
  EventContextArea,
  FunnelContextArea,
} from "../components/funnel/types";
import { UseBundlePurchaseFunnelOptions } from "../components/slideInMenu/hooks/useSlideInMenu";
import { useGetNavigationContext } from "./useGetNavigationContext";
import { useIsInReader } from "./useIsInReader";
import { setFunnelModalConfig } from "../components/funnel/FunnelModal";

/**
 * Hook to handle bundle purchase funnel launching with authentication flow
 */
export function useBundlePurchaseFunnel(
  options: UseBundlePurchaseFunnelOptions & {
    contextArea?: EventContextArea;
  } = {},
) {
  const { setFunnelConfig, contextArea } = options;
  const inReader = useIsInReader();

  // In reader: use global modal. Outside reader: use inline funnel in slide-in menu
  const funnelConfigSetter = inReader ? setFunnelModalConfig : setFunnelConfig;

  const accountData = useAccountData();
  const isLoggedIn = !!accountData?.user;

  // Generate navigation context if contextArea is provided
  const navigationContext = contextArea
    ? useGetNavigationContext({
        contextArea,
      })
    : undefined;

  const launchBundlePurchaseFunnel = useCallback(
    (bundle: Bundle, event?: React.MouseEvent) => {
      // Handle event if provided (for card clicks)
      event?.stopPropagation();

      if (!funnelConfigSetter) {
        console.error("No funnel config setter available");
        return;
      }

      const baseOptions = { isModal: true };

      if (!isLoggedIn) {
        // Show auth funnel first, then purchase modal
        funnelConfigSetter({
          type: FUNNEL_TYPES.signup,
          context: {
            modalContext: FunnelContextArea.default,
            navigationContext,
            bundle: bundle,
          },
          options: {
            ...baseOptions,
            onSuccess: () => {
              // After successful auth, show purchase modal
              funnelConfigSetter({
                type: FUNNEL_TYPES.purchaseBundle,
                context: {
                  modalContext: FunnelContextArea.default,
                  navigationContext,
                  bundle: bundle,
                },
                options: {
                  ...baseOptions,
                  onSuccess: () => {
                    funnelConfigSetter(undefined);
                  },
                },
              });
            },
          },
        });
      } else {
        // Show purchase modal directly
        funnelConfigSetter({
          type: FUNNEL_TYPES.purchaseBundle,
          context: {
            modalContext: FunnelContextArea.default,
            navigationContext,
            bundle: bundle,
          },
          options: {
            ...baseOptions,
            onSuccess: () => {
              funnelConfigSetter(undefined);
            },
          },
        });
      }
    },
    [isLoggedIn, funnelConfigSetter, navigationContext, inReader],
  );

  return {
    launchBundlePurchaseFunnel,
    isLoggedIn,
  };
}
